import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import SoKnoLogo from "@/components/SoKnoLogo";
import {
  FOOTER_EXTRA_LINKS,
  NAV_LINKS,
  linkHref,
  navTestId,
} from "@/constants/navLinks";
import { SOKNO_EMAILS } from "@/lib/api";
import { ANALYTICS_EVENTS, track } from "@/lib/analytics";

const FOOTER_EMAILS = [
  { id: "general", value: SOKNO_EMAILS.general },
  { id: "casting", value: SOKNO_EMAILS.casting },
  { id: "projects", value: SOKNO_EMAILS.projects },
  { id: "partners", value: SOKNO_EMAILS.partners },
];

/**
 * Shared site footer — sitemap + official emails (once) + studio line.
 * Used on the landing contact block and standalone pages (e.g. Crew & Production Submission).
 */
export default function SiteFooter({ showEmails = true }) {
  const location = useLocation();
  const navigate = useNavigate();
  const onHome = location.pathname === "/";

  const goToLink = (link) => (e) => {
    e.preventDefault();

    if (link.formHash) {
      navigate({ pathname: link.to, hash: link.formHash });
      return;
    }

    if (link.to && !link.hash) {
      if (location.pathname !== link.to) navigate(link.to);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    const hash = link.hash || "home";
    if (!onHome) {
      navigate({ pathname: "/", hash });
      return;
    }
    document.getElementById(hash)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <footer
      data-testid="site-footer"
      className="site-footer-tone section-tone-footer"
      style={{
        background: "var(--section-footer, #050403)",
        borderTop: "1px solid var(--sokno-line)",
        paddingTop: 60,
        paddingBottom: 60,
      }}
    >
      <div className="container-x">
        <div
          className="grid gap-10"
          style={{ gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}
        >
          <div>
            <SoKnoLogo size={140} />
            <p
              className="mt-5"
              style={{
                color: "var(--sokno-cream-dim)",
                fontSize: 13.5,
                lineHeight: 1.7,
                maxWidth: 340,
              }}
              data-testid="footer-tagline"
            >
              SoKno Productions — Opening doors to bold independent storytelling.
            </p>
          </div>

          <div>
            <div className="card-meta">Sitemap</div>
            <ul className="mt-4 flex flex-col gap-2">
              {[...NAV_LINKS, ...FOOTER_EXTRA_LINKS.filter((l) => l.label !== "Privacy")].map((l) => (
                <li key={l.label}>
                  <a
                    href={linkHref(l)}
                    onClick={goToLink(l)}
                    data-testid={navTestId(l.label, "footer-link")}
                    style={{
                      color: "var(--sokno-cream)",
                      textDecoration: "none",
                      fontSize: 13.5,
                    }}
                    className="hover:text-[var(--sokno-gold-warm)] transition-colors"
                  >
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {showEmails && (
            <div data-testid="footer-emails">
              <div className="card-meta">Connect</div>
              <ul className="mt-4 flex flex-col gap-2">
                {FOOTER_EMAILS.map((e) => (
                  <li key={e.value}>
                <a
                  href={`mailto:${e.value}`}
                  data-testid={`footer-email-${e.id}`}
                  style={{
                    color: "var(--sokno-cream)",
                    textDecoration: "none",
                    fontSize: 13.5,
                    wordBreak: "break-all",
                  }}
                  className="hover:text-[var(--sokno-gold-warm)] transition-colors"
                  onClick={() =>
                    track(ANALYTICS_EVENTS.OUTBOUND_LINK_CLICKED, {
                      cta_label: e.value,
                      source_category: "footer_email",
                      link_host: "mailto",
                    })
                  }
                >
                  {e.value}
                </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div>
            <div className="card-meta">Studio</div>
            <p className="mt-4" style={{ color: "var(--sokno-cream)", fontSize: 13.5 }}>
              Dallas, Texas
            </p>
            <p
              className="mt-1"
              style={{ color: "var(--sokno-cream-dim)", fontSize: 12.5 }}
            >
              Independent film. By intention.
            </p>
          </div>
        </div>

        <div className="hr-gold mt-12" />
        <div
          className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-3"
          style={{ color: "var(--sokno-muted)", fontSize: 12 }}
        >
          <span data-testid="footer-copyright">
            © {new Date().getFullYear()} SoKno Productions. Dallas, Texas. All rights reserved.
          </span>
          <div className="flex items-center gap-4">
            <a
              href="/privacy"
              data-testid="footer-privacy-link"
              style={{
                color: "var(--sokno-cream-dim)",
                textDecoration: "none",
                letterSpacing: "0.06em",
              }}
            >
              Privacy
            </a>
            <span
              className="font-script"
              style={{ fontSize: 10, color: "var(--sokno-gold)" }}
            >
              Opening doors to bold independent storytelling
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}
